import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";

export const PAGE_SIZE = 200;

function scrollAnchor(scroll: HTMLDivElement) {
  const top = scroll.getBoundingClientRect().top;
  const element = Array.from(
    scroll.querySelectorAll<HTMLElement>("[data-chat-entry-id]")
  ).find((entry) => entry.getBoundingClientRect().bottom > top);
  return {
    id: element?.dataset.chatEntryId,
    offset: (element?.getBoundingClientRect().top ?? top) - top,
  };
}

export function useChatHistoryScroll(
  scrollRef: RefObject<HTMLDivElement | null>,
  entries: readonly { id: string; entryType: string }[],
  loadOlder: (() => void) | undefined,
  loadingOlder = false
) {
  const positionRef = useRef<
    | ({ firstId: string | undefined } & ReturnType<typeof scrollAnchor>)
    | undefined
  >(undefined);
  const requestedRef = useRef<typeof entries | undefined>(undefined);
  useEffect(() => {
    // The composer derives plan mode and effort from the latest user message.
    if (
      loadOlder &&
      entries.length &&
      !entries.some((e) => e.entryType === "UserMessage") &&
      requestedRef.current !== entries
    ) {
      const scroll = scrollRef.current;
      if (scroll)
        positionRef.current = {
          firstId: entries[0]?.id,
          ...scrollAnchor(scroll),
        };
      requestedRef.current = entries;
      loadOlder();
    }
  }, [entries, loadOlder, scrollRef]);
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const onScroll = () => {
      if (positionRef.current)
        positionRef.current = {
          firstId: positionRef.current.firstId,
          ...scrollAnchor(scroll),
        };
      if (
        scroll.scrollTop <= 1 &&
        loadOlder &&
        requestedRef.current !== entries
      ) {
        positionRef.current = {
          firstId: entries[0]?.id,
          ...scrollAnchor(scroll),
        };
        requestedRef.current = entries;
        loadOlder();
      }
    };
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) onScroll();
    };
    scroll.addEventListener("scroll", onScroll, { passive: true });
    scroll.addEventListener("wheel", onWheel, { passive: true });
    scroll.addEventListener("touchmove", onScroll, { passive: true });
    return () => {
      scroll.removeEventListener("scroll", onScroll);
      scroll.removeEventListener("wheel", onWheel);
      scroll.removeEventListener("touchmove", onScroll);
    };
  }, [scrollRef, entries, loadOlder]);
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const previous = positionRef.current;
    if (!scroll || !previous) return;
    const anchor = previous.id
      ? scroll.querySelector<HTMLElement>(
          `[data-chat-entry-id="${CSS.escape(previous.id)}"]`
        )
      : null;
    if (anchor && entries.findIndex((e) => e.id === previous.firstId) > 0)
      scroll.scrollTop +=
        anchor.getBoundingClientRect().top -
        scroll.getBoundingClientRect().top -
        previous.offset;
    if (!loadingOlder) positionRef.current = undefined;
    else if (entries.length)
      positionRef.current = {
        firstId: entries[0]?.id,
        ...scrollAnchor(scroll),
      };
  }, [entries, scrollRef, loadOlder, loadingOlder]);
}
